import { OrganizationThemeService } from '@/services';
import { DEFAULT_URI } from '@/constants';
import Snackbar from '@/classes/snackbar';
import set from 'lodash/set';

const ERROR_SAVING_THEME = 'errors.savingTheme';
const defaultBaseColors = {
  accent: '#ffc107',
  error: '#f44336',
  info: '#77a8da',
  success: '#4ea96d',
  warning: '#fccc4a'
};
export const defaultTheme = {
  light: {
    primary: '#1976d2',
    secondary: '#B6B6B6',
    surface: '#D6D6D6'
  },
  dark: {
    primary: '#f47920',
    secondary: '#5d7783',
    surface: '#191919'
  }
};

export const initialOptionalFields = {
  customDateField: {
    display: true,
    label: 'Custom Date Field'
  },
  customNumberField: {
    display: true,
    label: 'Custom Number Field'
  }
};

const handleThemeActionMessage = ({ dispatch, message, type }) => {
  dispatch('createSnackbarMessage', {
    type,
    message: {
      i18nKey: message
    }
  });
};

const handleThemeSaveSuccess = ({ commit, newState, vuetify }) => {
  commit('setTheme', { newState, vuetify });
};

export default {
  setThemeColor: ({ dispatch, getters }, { payload, vuetify }) => {
    const darkMode = getters.theme.darkMode ? 'dark' : 'light';
    dispatch('saveTheme', {
      payload: {
        [`${darkMode}.${payload.key}`]: payload.color
      },
      vuetify
    });
  },

  changeThemeMode: ({ dispatch }, { payload, vuetify }) => {
    dispatch('saveTheme', {
      payload: {
        darkMode: payload
      },
      vuetify
    });
  },

  setLogo: ({ dispatch }, { payload, vuetify }) => {
    dispatch('saveTheme', {
      payload: {
        logo: payload
      },
      vuetify
    });
  },

  setFont: ({ dispatch }, { payload, vuetify }) => {
    dispatch('saveTheme', {
      payload: {
        font: payload
      },
      vuetify
    });
  },

  getStoredTheme: async ({ commit, dispatch }, { payload, vuetify }) => {
    const { organizationId, uri } = payload;
    try {
      const response = await OrganizationThemeService.getTheme(organizationId, uri);
      commit('setTheme', { newState: response, vuetify });
      return response;
    } catch (e) {
      if (e.response.status === 404) {
        dispatch('createNewTheme', { payload: { organizationId, uri }, vuetify });
      }
      return null;
    }
  },

  saveTheme: ({ getters, dispatch }, { payload, vuetify }) => {
    const businessProcesses = getters.selectedBusinessProcesses;
    if (businessProcesses.length === 1) {
      return dispatch('updateStoredTheme', { payload: { uri: businessProcesses[0], newTheme: payload }, vuetify });
    } else {
      return dispatch('saveMultipleThemes', { payload: { uris: businessProcesses, newTheme: payload }, vuetify });
    }
  },

  saveNewTheme: async ({ commit }, { payload, vuetify }) => {
    const response = await OrganizationThemeService.createTheme(payload.theme);
    if (response) {
      commit('setTheme', { payload: { newState: response }, vuetify });
    }
    return response;
  },

  updateStoredTheme: async ({ dispatch, commit, getters }, { payload, vuetify }) => {
    const theme = { ...getters.theme };
    const { uri, newTheme } = payload;
    const updatedBy = getters.username;
    const orgId = theme.organizationId;
    try {
      const response = await OrganizationThemeService.updateTheme(orgId, uri, {
        ...newTheme,
        updatedBy
      });
      if (response && response._id) {
        handleThemeSaveSuccess({ commit, newState: response, vuetify });
        return response;
      } else {
        throw new Error();
      }
    } catch {
      handleThemeActionMessage({ dispatch, message: ERROR_SAVING_THEME, type: Snackbar.TYPE.ERROR });
      return null;
    }
  },

  createDefaultThemeForBp: async ({ dispatch, getters }, { payload, vuetify }) => {
    const { organizationId, uri } = payload;
    try {
      const response = await OrganizationThemeService.getTheme(organizationId, DEFAULT_URI);
      delete response._id;
      delete response.__v;
      delete response.updatedAt;
      delete response.createdAt;
      dispatch('saveNewTheme', {
        payload: {
          theme: {
            ...response,
            updatedBy: getters.username,
            uri
          }
        },
        vuetify
      });
    } catch (e) {
      handleThemeActionMessage({ dispatch, message: ERROR_SAVING_THEME, type: Snackbar.TYPE.ERROR });
    }
  },

  createNewEmptyTheme: ({ dispatch, getters }, { payload, vuetify }) => {
    const { organizationId, uri } = payload;
    const orgTheme = {
      organizationId,
      uri,
      dark: {
        ...defaultBaseColors,
        ...defaultTheme.dark
      },
      light: {
        ...defaultBaseColors,
        ...defaultTheme.light
      },
      darkMode: false,
      logo: 'empty',
      font: 'Roboto',
      updatedBy: getters.username,
      overrideDefaultTheme: false,
      optionalFields: initialOptionalFields
    };
    dispatch('saveNewTheme', { payload: { theme: orgTheme }, vuetify });
  },

  createNewTheme: ({ dispatch }, { payload, vuetify }) => {
    if (payload.uri === DEFAULT_URI) {
      dispatch('createNewEmptyTheme', { payload, vuetify });
    } else {
      dispatch('createDefaultThemeForBp', { payload, vuetify });
    }
  },

  saveMultipleThemes: async ({ dispatch, getters, commit }, { payload, vuetify }) => {
    const { uris, newTheme } = payload;
    const updatedBy = getters.username;
    let returnValue = null;
    const theme = { ...getters.theme };
    Object.keys(newTheme).forEach(key => {
      set(theme, key, newTheme[key]);
    });
    theme.updatedBy = updatedBy;
    const organizationId = getters.organizationId;
    try {
      const response = await OrganizationThemeService.saveMultipleThemes(organizationId, uris, theme);
      if (response) {
        handleThemeSaveSuccess({ commit, newState: response, vuetify });
        returnValue = response;
      }
    } catch {
      handleThemeActionMessage({ dispatch, message: ERROR_SAVING_THEME, type: Snackbar.TYPE.ERROR });
    }
    return returnValue;
  },

  updateOptionalField: ({ dispatch }, { payload, vuetify }) => {
    const { field, property, value } = payload;
    return dispatch('saveTheme', {
      payload: {
        [`optionalFields.${field}.${property}`]: value
      },
      vuetify
    });
  },

  updateOverrideDefaultTheme: async ({ dispatch, getters, commit }, { payload, vuetify }) => {
    const organizationId = getters.organizationId;
    let uri = getters.selectedBusinessProcesses[0];
    if (!payload) {
      uri = DEFAULT_URI;
    }
    const saveTheme = dispatch('saveTheme', { payload: { overrideDefaultTheme: payload }, vuetify });
    const getStoredTheme = dispatch('getStoredTheme', { payload: { organizationId, uri }, vuetify });
    const actionDispatchs = [saveTheme, getStoredTheme];
    Promise.all(actionDispatchs).then(([, getStoredThemeResponse]) => {
      commit('setTheme', { newState: getStoredThemeResponse, vuetify });
    });
  },

  deleteTheme: async ({ getters, dispatch }, { payload, vuetify }) => {
    const organizationId = getters.organizationId;
    try {
      await OrganizationThemeService.deleteTheme(organizationId, payload, getters.username);
      handleThemeActionMessage({ dispatch, message: 'success.deletedTheme', type: Snackbar.TYPE.SUCCESS });
      const selectedBusinessProcessUri = getters.selectedBusinessProcesses[0];
      if (selectedBusinessProcessUri === payload) {
        dispatch('getStoredTheme', { payload: { organizationId, uri: payload }, vuetify });
      }
    } catch (e) {
      handleThemeActionMessage({ dispatch, message: 'errors.deletingTheme', type: Snackbar.TYPE.ERROR });
    }
  }
};
